import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';

import { JwtHelperService } from '@auth0/angular-jwt';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogModalComponent } from '@core/components/dialog-modal/dialog-modal.component';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private static tokenJwt: string;
  private static devolverUserId: any;
  private static devolverVerificado: boolean;
  private userLog = new BehaviorSubject(null);
  public userLog$ = this.userLog.asObservable();
  public userSignal = signal(null);
  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  //chequea si el mail ingresado existe en la base
  checkUserByMail(data) {
    return this.http.post(`${environment.apiGetNet}clientes/cliente_usuario_web`, data);
  }

  registerNewUser(cliente_web_infoRequest) {
    return this.http.post(
      `${environment.apiGetNet}clientes/cliente_usuario_web/add`,
      cliente_web_infoRequest,
    );
  }

  loginUser(data) {
    const cliente_usuario_web_loguin_infoRequest = {
      hash_id: data.id,
      clave: data.clave,
    };
    return this.manejoRenovacionToken(
      this.http.post(
        `${environment.apiGetNet}api/login/authenticate`,
        cliente_usuario_web_loguin_infoRequest,
        { withCredentials: true },
      ),
    );
  }
  public get loggedIn(): boolean {
    return !!UserService.tokenJwt;
  }

  public static getUser(): number {
    return UserService.devolverUserId;
  }
  public static deleteUser() {
    UserService.devolverUserId = null;
  }

  public static getUserValidado(): boolean {
    return UserService.devolverVerificado;
  }

  public static obtenerToken() {
    return UserService.tokenJwt;
  }
  //funcion generica para armar el options con el Bearer token
  public static devolverHeader() {
    let token = UserService.obtenerToken();
    if (token) {
      const httpOptions = {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      };
      return httpOptions;
    }
    return null;
  }
  //Envio al observable la dato del token decodificada
  private manejoRenovacionToken(observable$: Observable<any>): Observable<any> {
    const helper = new JwtHelperService();
    return observable$.pipe(
      tap(async (x) => {
        UserService.tokenJwt = x;
        const data = {
          token: x,
          expirationDate: helper.getTokenExpirationDate(x),
          decodedToken: helper.decodeToken(x),
          isExpired: helper.isTokenExpired(x),
        };
        console.log(data);
        await UserService.tokenJwt;
        UserService.devolverUserId = data.decodedToken.id;
        UserService.devolverVerificado = data.decodedToken.verificado;
        this.startRefreshTokenTimer();
        this.userLog.next(data.decodedToken);
        this.userSignal.set(data.decodedToken);
      }),
      catchError((x) => {
        UserService.tokenJwt = '';
        this.logOut();
        return throwError('No se pudo renovar el token');
      }),
    );
  }

  public tryLogin(state?: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.openLoginModal().pipe(
      switchMap((x) => {
        return this.renovarToken().pipe(
          map((p) => {
            return p ?
                this.router.createUrlTree([state?.url || this.router.url])
              : this.router.createUrlTree(['/']);
          }),
          catchError((x) => {
            return of(this.router.createUrlTree(['/']));
          }),
        );
      }),
    );
  }

  public openLoginModal(): Observable<any> {
    const dialogRef = this.dialog.open(DialogModalComponent, {
      width: '580px',
      height: 'auto',
      maxWidth: '100vw',
      disableClose: true,
      panelClass: ['old-inputs'],
      data: { type: 'login' },
    });

    return dialogRef.afterClosed();
  }
  //Renueva el token para que el usuario no tenga que iniciar sesion nuevamente
  public renovarToken(): Observable<any> {
    let token = UserService.tokenJwt;
    return this.manejoRenovacionToken(
      this.http.post(`${environment.apiGetNet}api/login/renovarToken`, token, {
        withCredentials: true,
      }),
    );
  }

  private refreshTokenTimeout;
  private startRefreshTokenTimer() {
    const helper = new JwtHelperService();
    const jwtTokenExp = helper.getTokenExpirationDate(UserService.obtenerToken());
    const timeout = jwtTokenExp.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = this.refreshTokenTimeout = setTimeout(
      () => this.renovarToken().subscribe({ error: () => this.tryLogin() }),
      timeout,
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
  //Envia el codigo de verificacion al mail para habilitar el cambio de contraseña
  getNewPass(cliente_usuario_web_infoRequest) {
    return this.http.put(
      `${environment.apiGetNet}clientes/cliente_usuario_web/psw/reset`,
      cliente_usuario_web_infoRequest,
    );
  }
  //Cambia la password
  changePassword(cliente_usuario_web_clave_infoRequest) {
    return this.http.put(
      `${environment.apiGetNet}clientes/cliente_usuario_web/psw/change`,
      cliente_usuario_web_clave_infoRequest,
    );
  }

  logOut() {
    let body = null;
    let tokenJwt = UserService.tokenJwt;
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${tokenJwt}`,
      }),
      withCredentials: true,
    };
    this.http
      .post(`${environment.apiGetNet}api/login/eliminarTokenJWT`, body, httpOptions)
      .subscribe((res) => {
        this.userLog.next(null);
        this.userSignal.set(null);
        this.stopRefreshTokenTimer();
        UserService.deleteUser();
        UserService.devolverUserId = null;
        UserService.tokenJwt = null;
      });

    let token = null;
    this.userLog.next(null);
    this.userSignal.set(null);

    this.stopRefreshTokenTimer();
    UserService.deleteUser();
    UserService.devolverUserId = null;
    UserService.tokenJwt = null;
  }
  //Envia un mail con el hash de validacion
  validarMail(cliente_usuario_web_loguin_infoRequest): Observable<any> {
    return this.http.post(
      `${environment.apiGetNet}clientes/cliente_usuario_web/verify/link`,
      cliente_usuario_web_loguin_infoRequest,
    );
  }
  //Envia el hash que recibio por mail para validar
  verificacionMailHash(data): Observable<any> {
    return this.http.post(
      `${environment.apiGetNet}clientes/cliente_usuario_web/verify/set?hash`,
      data,
    );
  }

  getReservaEnProceso() {
    let options = UserService.devolverHeader();
    let cliente = UserService.devolverUserId;
    console.log(cliente);
    return this.http.get(
      `${environment.apiGetNet}mi_cuenta/presupuestos/en_proceso?id_cliente=${cliente}`,
      options,
    );
  }
}
